import React, { useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Cell,
} from "recharts";

import { MaxChartSize, VolunteerGroupableField } from "../../data/types";
import { CHART_COLORS, CHART_HEIGHT_MAP, CHART_WIDTH_CLASSES } from "../../data";

type categoryType = { field: string, value: string }

type VolunteerBarChartProps = {
    title: string;
    data: { name: string; count: number }[];
    groupBy: VolunteerGroupableField;
    width?: MaxChartSize;
    height?: MaxChartSize;
    onChartClick?: (category: categoryType) => void;
    // NEW PROPS FOR CONTROL
    tickAngle?: number;   // e.g. -45
    minBarWidth?: number; // e.g. 60 (pixels per bar)
};

export function BarChartComponent({
    title,
    data,
    groupBy,
    width = 'xl',
    height = 'lg',
    onChartClick,
    // Default values for angle and spread
    tickAngle = -30,
    minBarWidth = 60
}: VolunteerBarChartProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const chartHeight = CHART_HEIGHT_MAP[height];
    const widthClass = CHART_WIDTH_CLASSES[width];

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => b.count - a.count);
    }, [data]);

    const totalCount = sortedData.reduce((s, d) => s + d.count, 0);

    const minChartWidth = sortedData.length * minBarWidth;

    const handleBarClick = (bar: any) => {
        if (onChartClick && bar && bar.payload) {
            onChartClick({ field: groupBy, value: bar.payload.name });
        }
    };

    const CustomBarLabel = (props: any) => {
        const { x, y, width, value } = props;
        return (
            <text
                x={x + width / 2}
                y={y}
                dy={-6}
                fill="#374151"
                textAnchor="middle"
                fontSize={12}
                fontWeight="bold"
            >
                {value}
            </text>
        );
    };

    const CustomAngledTick = (props: any) => {
        const { x, y, payload } = props;
        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dy={16}
                    textAnchor="end"
                    fill="#6B7280"
                    fontSize={12}
                    // Use the dynamic variable here
                    transform={`rotate(${tickAngle})`}
                >
                    {payload.value}
                </text>
            </g>
        );
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 p-3 shadow-xl rounded-lg border border-gray-100 dark:border-gray-700">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">{label}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: payload[0].payload.fill || payload[0].fill }}
                        />
                        <span>{payload[0].value} Volunteers</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div
            className={`
                bg-white dark:bg-gray-600 rounded-xl shadow-sm border p-6
                dark:border-gray-500 mx-auto w-full ${widthClass}
            `}
        >
            <div className="mb-2 flex items-center justify-between gap-4">
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-50 tracking-tight">
                    {title}
                </h2>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                    Total: {totalCount}
                </span>
            </div>

            <div className="border-b border-gray-200 dark:border-gray-600 mb-6" />

            <div className="w-full overflow-x-auto pb-2">
                {/* Apply the calculated minimum width here */}
                <div style={{ minWidth: `${minChartWidth}px`, height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={sortedData}
                            // Adjust bottom margin based on angle to prevent clipping
                            // steeper angles need more bottom margin
                            margin={{
                                top: 20,
                                right: 10,
                                left: -20,
                                bottom: Math.abs(tickAngle) > 45 ? 100 : 80
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#E5E7EB"
                                className="dark:stroke-gray-600"
                            />

                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={<CustomAngledTick />}
                                interval={0}
                                height={60}
                            />

                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                allowDecimals={false}
                            />

                            <Tooltip cursor={{ fill: 'transparent' }} content={<CustomTooltip />} />

                            <Bar
                                dataKey="count"
                                radius={[4, 4, 0, 0]}
                                cursor="pointer"
                                onClick={handleBarClick}
                                onMouseEnter={(_, index) => setActiveIndex(index)}
                                onMouseLeave={() => setActiveIndex(null)}
                                maxBarSize={60}
                                label={<CustomBarLabel />}
                            >
                                {sortedData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                                        fillOpacity={activeIndex === null || activeIndex === index ? 1 : 0.4}
                                        className="transition-all duration-300"
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}